import React, { useState, useEffect } from "react";
import { API_BASE } from "../../config/api";
import toast from "react-hot-toast";
import "./DeleteDossierModal.css";

// Suppression d'un dossier : 2 étapes. (1) prévisualisation NON destructive de ce qui sera
// effacé (MySQL + fichiers + Mongo + galerie + Vast) ; (2) saisie du mot de passe UNIQUE.
// L'appel réel n'est possible que si le serveur a le mot de passe configuré (sinon 403).
const DeleteDossierModal = ({ dossier, user, onClose, onDeleted }) => {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const [step, setStep] = useState(1);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await fetch(`${API_BASE}/api/dossiers/${dossier.id}/delete-preview`);
        const j = await r.json();
        if (alive) {
          if (j.success) setPreview(j.preview);
          else toast.error(j.message || "Prévisualisation indisponible");
        }
      } catch (_) {
        if (alive) toast.error("Erreur réseau");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [dossier.id]);

  const doDelete = async () => {
    if (!pw) { toast.error("Mot de passe requis"); return; }
    setBusy(true);
    try {
      const r = await fetch(`${API_BASE}/api/dossiers/${dossier.id}/delete`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password: pw, by: (user && user.email) || "" }),
      });
      const j = await r.json();
      if (r.ok && j.success) {
        toast.success(j.message || "Dossier supprimé");
        onDeleted(dossier.id);
      } else if (r.status === 403) {
        toast.error(j.message || "Suppression désactivée côté serveur");
      } else if (r.status === 401) {
        toast.error("Mot de passe de suppression incorrect");
      } else {
        toast.error(j.message || "Échec de la suppression");
      }
    } catch (_) {
      toast.error("Erreur réseau");
    } finally {
      setBusy(false);
    }
  };

  const mysqlRows = preview ? Object.entries(preview.mysql || {}).filter(([, v]) => v) : [];
  const mysqlTotal = mysqlRows.reduce((s, [, v]) => s + v, 0);

  return (
    <div className="del-overlay" onClick={onClose}>
      <div className="del-box" onClick={(e) => e.stopPropagation()}>
        <h2 className="del-title">Supprimer définitivement</h2>
        <p className="del-sub">
          Dossier <strong>{dossier.numero_dossier}</strong>
          {dossier.patient ? <> — {dossier.patient}</> : null}
        </p>

        {loading ? (
          <p className="del-loading">Analyse du dossier…</p>
        ) : !preview ? (
          <p className="del-loading">Prévisualisation indisponible.</p>
        ) : (
          <>
            <div className="del-preview">
              <p className="del-warn">Action <strong>irréversible</strong>. Seront effacés :</p>
              <ul>
                <li><b>Base MySQL</b> : {mysqlTotal} enregistrement(s){mysqlRows.length ? ` — ${mysqlRows.map(([k, v]) => `${k} (${v})`).join(", ")}` : ""}</li>
                <li><b>Fichiers / scans</b> : {(preview.fichiers || []).length} sur le disque{preview.dossiers_disque && preview.dossiers_disque.length ? ` — ${preview.dossiers_disque.join(", ")}` : ""}</li>
                <li><b>MongoDB</b> : {preview.mongo_dossier || 0} dossier, {preview.galerie_images || 0} photo(s), {preview.galerie_videos || 0} vidéo(s)</li>
                <li><b>Serveur IA (Vast)</b> : nettoyage programmé (copie Mongo + index RAG + scans)</li>
              </ul>
              {!preview.delete_active && (
                <p className="del-disabled">🔒 La suppression est actuellement <b>désactivée</b> côté serveur (mot de passe non configuré).</p>
              )}
            </div>

            {step === 1 ? (
              <div className="del-actions">
                <button className="del-cancel" onClick={onClose}>Annuler</button>
                <button className="del-next" disabled={!preview.delete_active} onClick={() => setStep(2)}>
                  Continuer
                </button>
              </div>
            ) : (
              <div className="del-confirm">
                <label className="del-field">
                  <span>Mot de passe de suppression</span>
                  <input type="password" value={pw} autoFocus
                         placeholder="Mot de passe unique"
                         onChange={(e) => setPw(e.target.value)}
                         onKeyDown={(e) => { if (e.key === "Enter" && pw) doDelete(); }} />
                </label>
                <div className="del-actions">
                  <button className="del-cancel" onClick={onClose} disabled={busy}>Annuler</button>
                  <button className="del-danger" onClick={doDelete} disabled={busy || !pw}>
                    {busy ? "Suppression…" : "Supprimer définitivement"}
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default DeleteDossierModal;
